import React, { useState } from 'react';
import axios from 'axios';
import Loader from '../utils/Loader';
import { formatCurrency, formatCurrencyDual, formatUsdToLocal } from '../../data/constants';

const API = process.env.REACT_APP_Backend_Host_Name;

const DisputeSection = ({
  dispute,
  orderId,
  orderSummary,
  isSeller,
  currency,
  exchangeRate,
  onUpdate
}) => {
  const [refundAmount, setRefundAmount] = useState('');
  const [disputeMessage, setDisputeMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const role = isSeller ? 'seller' : 'buyer';
  const orderTotal = orderSummary?.total ?? 0;
  const maxRefund = orderSummary ? (orderSummary.total - (orderSummary.serviceFee || 0)) : 0;
  const isResolved = dispute?.status === 'resolved';
  const adminInvolved = dispute?.status === 'admin_involved';
  const hasProposal = dispute?.proposedResolution && !isResolved;

  const handleRequestAdmin = async () => {
    if (!window.confirm('Request admin involvement? This will escalate the dispute.')) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/v1/orders/dispute/request-admin`,
        { orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate?.();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to request admin');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProposeResolution = async (e) => {
    e.preventDefault();
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount < 0) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/v1/orders/dispute/propose-resolution`,
        { orderId, refundToBuyer: amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefundAmount('');
      onUpdate?.();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to propose resolution');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptResolution = async () => {
    if (!window.confirm('Accept this resolution? This will finalize the dispute.')) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/v1/orders/dispute/accept-resolution`,
        { orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate?.();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to accept resolution');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!disputeMessage.trim()) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/v1/orders/dispute/message`,
        { orderId, text: disputeMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDisputeMessage('');
      onUpdate?.();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const displayReason = dispute?.reason === 'Other' ? dispute?.customReason : dispute?.reason;

  return (
    <div className="disputeSection">
      <div className="disputeSectionHeader">
        <h3>Dispute Management</h3>
        <span className={`disputeSectionStatus ${dispute?.status || 'negotiating'}`}>
          {dispute?.status === 'negotiating' && 'Negotiating'}
          {dispute?.status === 'admin_involved' && 'Admin Involved'}
          {dispute?.status === 'resolved' && 'Resolved'}
        </span>
      </div>
      <div className="disputeSectionBody">
      <div className="disputeSectionReason">
        <strong>Started by:</strong> {dispute?.initiatedBy === 'buyer' ? 'Buyer' : 'Seller'} &bull;{' '}
        <strong>Reason:</strong> {displayReason || 'N/A'}
      </div>

      {dispute?.status === 'resolved' && dispute?.resolution && (
        <div className="disputeResolutionBox">
          <p><strong>Resolved by:</strong> {dispute.resolution.resolvedBy === 'admin' ? 'Admin' : 'Mutual agreement'}</p>
          <p>Refund to buyer: {formatCurrencyDual(dispute.resolution.refundToBuyer, currency, exchangeRate)}</p>
          {dispute.resolution.sellerGot != null && (
            <p>Seller received: {formatCurrencyDual(dispute.resolution.sellerGot, currency, exchangeRate)}</p>
          )}
          {dispute.resolution.adminNote && <p><em>{dispute.resolution.adminNote}</em></p>}
        </div>
      )}

      {!isResolved && (
        <>
          {hasProposal && (
            <div className="disputeProposalBox">
              <p>
                <strong>{dispute.proposedResolution.proposedBy === 'buyer' ? 'Buyer' : 'Seller'}</strong> proposed:
                Refund {formatCurrencyDual(dispute.proposedResolution.refundToBuyer, currency, exchangeRate)} to buyer
              </p>
              {dispute.proposedResolution.proposedBy !== role && (
                <div className="disputeProposalActions">
                  <button
                    className="modalButton submit"
                    onClick={handleAcceptResolution}
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader type="miniPlus" /> : 'Accept'}
                  </button>
                  <button
                    className="modalButton cancel"
                    onClick={handleRequestAdmin}
                    disabled={isLoading}
                  >
                    Request Admin
                  </button>
                </div>
              )}
              {dispute.proposedResolution.proposedBy === role && (
                <p style={{ fontSize: '0.85rem', color: '#666' }}>Waiting for the other party to accept.</p>
              )}
            </div>
          )}

          {!adminInvolved && !hasProposal && (
            <form onSubmit={handleProposeResolution} className="disputeProposalBox">
              <p>
                <strong>Propose a resolution</strong> — Enter the refund amount to offer the buyer:
              </p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' }}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={maxRefund}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="Refund amount"
                  className="inputField"
                  style={{ maxWidth: '140px' }}
                />
                <button
                  type="submit"
                  className="modalButton submit"
                  disabled={isLoading || !refundAmount}
                >
                  {isLoading ? <Loader type="miniPlus" /> : 'Propose'}
                </button>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '8px' }}>
                Max refund: {formatCurrencyDual(maxRefund, currency, exchangeRate)}
              </p>
              {currency !== 'USD' && parseFloat(refundAmount) > 0 && (
                <span className="cpLocalPreview">{formatUsdToLocal(refundAmount, currency, exchangeRate)}</span>
              )}
            </form>
          )}

          {!adminInvolved && (
            <div style={{ marginTop: '12px' }}>
              <button
                type="button"
                className="modalButton cancel"
                onClick={handleRequestAdmin}
                disabled={isLoading}
              >
                {isLoading ? <Loader type="miniPlus" /> : 'Request Admin Involvement'}
              </button>
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '6px' }}>
                If you cannot reach an agreement, either party can request admin to resolve the dispute.
              </p>
            </div>
          )}

          {adminInvolved && (
            <p style={{ padding: '12px', background: '#e3f2fd', borderRadius: '8px', fontSize: '0.9rem', color: '#1565c0' }}>
              Admin has been involved. The dispute is under review. You will be notified when it is resolved.
            </p>
          )}

          {dispute?.messages?.length > 0 && (
            <div className="disputeMessages" style={{ marginTop: '16px' }}>
              <strong style={{ fontSize: '0.85rem' }}>Dispute activity</strong>
              {dispute.messages.map((msg, i) => (
                <div
                  key={i}
                  className={`disputeMessageItem ${msg.senderRole}`}
                >
                  <div className="disputeMsgRole">{msg.senderRole}</div>
                  {msg.text}
                </div>
              ))}
            </div>
          )}

          {!isResolved && (
            <form onSubmit={handleSendMessage} style={{ marginTop: '10px' }}>
              <textarea
                value={disputeMessage}
                onChange={(e) => setDisputeMessage(e.target.value)}
                placeholder="Add a message to the dispute..."
                className="disputeTextarea"
                rows={2}
                style={{ marginBottom: '8px' }}
              />
              <button
                type="submit"
                className="modalButton submit"
                disabled={isLoading || !disputeMessage.trim()}
              >
                {isLoading ? <Loader type="miniPlus" /> : 'Send Message'}
              </button>
            </form>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export default DisputeSection;
